import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  Landmark,
  FileText,
  Edit2,
  X,
  CheckCircle2,
  AlertCircle,
  Store,
  Package,
  Star,
  Copy,
  Check,
  Eye,
  EyeOff,
  ChevronRight,
  Bell,
  Settings,
  HelpCircle,
  Wallet,
  PlusCircle,
  ShoppingBag,
  DollarSign,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { sellerAPI } from "../API/axios";

interface SellerProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emailVerified: boolean;
  sellerProfile: {
    gstin: string;
    panNumber: string;
    businessAddress: string;
    bankAccount: string;
    ifscCode: string;
    businessName?: string;
    businessType?: string;
    totalProducts?: number;
    totalOrders?: number;
    totalRevenue?: number;
    rating?: number;
  };
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  businessAddress: string;
  businessName: string;
  businessType: string;
}

interface BankFormData {
  bankAccount: string;
  ifscCode: string;
  otp: string;
}

export default function SellerProfilePage() {
  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [bankUpdating, setBankUpdating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  const [showAccount, setShowAccount] = useState(false);

  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    firstName: "",
    lastName: "",
    phone: "",
    businessAddress: "",
    businessName: "",
    businessType: "",
  });

  const [bankForm, setBankForm] = useState<BankFormData>({
    bankAccount: "",
    ifscCode: "",
    otp: "",
  });

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  // OTP cooldown timer
  useEffect(() => {
    let timer: any;
    if (otpCooldown > 0) {
      timer = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpCooldown]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await sellerAPI.getProfile();
      setProfile(response.data);

      // Initialize profile form with current data
      setProfileForm({
        firstName: response.data.firstName || "",
        lastName: response.data.lastName || "",
        phone: response.data.phone?.replace("+91", "") || "",
        businessAddress: response.data.sellerProfile?.businessAddress || "",
        businessName: response.data.sellerProfile?.businessName || "",
        businessType: response.data.sellerProfile?.businessType || "",
      });
    } catch (err: any) {
      console.error("Fetch profile error:", err);
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
      } else {
        toast.error(err.response?.data?.message || "Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Copied to clipboard!");
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await sellerAPI.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        phone: profileForm.phone,
        businessAddress: profileForm.businessAddress,
      });

      await fetchProfile(); // Refresh profile data
      setShowEditModal(false);
      toast.success("Profile updated successfully!");
    } catch (err: any) {
      console.error("Update profile error:", err);
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleRequestBankOTP = async () => {
    // Validate bank form
    if (!bankForm.bankAccount || bankForm.bankAccount?.length < 9) {
      toast.error("Please enter a valid account number");
      return;
    }
    if (!bankForm.ifscCode.match(/^[A-Z]{4}0[A-Z0-9]{6}$/)) {
      toast.error("Please enter a valid IFSC code");
      return;
    }

    setOtpLoading(true);
    try {
      await sellerAPI.requestBankOTP();
      setOtpSent(true);
      setOtpCooldown(30);
      toast.info("OTP sent to your registered email");
    } catch (err: any) {
      console.error("Request OTP error:", err);
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleBankUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bankForm.otp || bankForm.otp?.length < 4) {
      toast.error("Please enter valid OTP");
      return;
    }

    setBankUpdating(true);
    try {
      await sellerAPI.updateBankDetails({
        bankAccount: bankForm.bankAccount,
        ifscCode: bankForm.ifscCode,
        otp: bankForm.otp,
      });

      await fetchProfile(); // Refresh profile data
      setShowBankModal(false);
      setOtpSent(false);
      setBankForm({ bankAccount: "", ifscCode: "", otp: "" });
      toast.success("Bank details updated successfully!");
    } catch (err: any) {
      console.error("Update bank error:", err);
      toast.error(
        err.response?.data?.message || "Failed to update bank details",
      );
    } finally {
      setBankUpdating(false);
    }
  };

  const handleCloseBankModal = () => {
    setShowBankModal(false);
    setOtpSent(false);
    setBankForm({ bankAccount: "", ifscCode: "", otp: "" });
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  };

  // Stats
  const stats = profile
    ? [
        {
          icon: Package,
          label: "Total Products",
          value: profile.sellerProfile?.totalProducts || 0,
          color: "text-primary",
          bg: "bg-primary/5",
        },
        {
          icon: ShoppingBag,
          label: "Total Orders",
          value: profile.sellerProfile?.totalOrders?.toLocaleString() || "0",
          color: "text-secondary",
          bg: "bg-secondary/5",
        },
        {
          icon: DollarSign,
          label: "Total Revenue",
          value: profile.sellerProfile?.totalRevenue
            ? "₹" +
              (profile.sellerProfile.totalRevenue / 100000).toFixed(1) +
              "L"
            : "₹0",
          color: "text-emerald-500",
          bg: "bg-emerald-50",
        },
        {
          icon: Star,
          label: "Avg Rating",
          value: (profile.sellerProfile?.rating || 0) + "/5",
          color: "text-amber-500",
          bg: "bg-amber-50",
        },
      ]
    : [];

  const quickActions = [
    {
      icon: PlusCircle,
      label: "Add New Product",
      color: "text-primary",
      bg: "bg-primary/5",
    },
    {
      icon: ShoppingBag,
      label: "View Orders",
      color: "text-secondary",
      bg: "bg-secondary/5",
    },
    {
      icon: Wallet,
      label: "Withdraw Earnings",
      color: "text-emerald-500",
      bg: "bg-emerald-50",
    },
    {
      icon: HelpCircle,
      label: "Get Support",
      color: "text-amber-500",
      bg: "bg-amber-50",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="glass-card max-w-md w-full !p-10 text-center flex flex-col items-center gap-4">
          <AlertCircle size={48} className="text-red-500" />
          <h3 className="text-xl font-extrabold text-slate-900 tracking-tight">
            Failed to load profile
          </h3>
          <p className="text-sm font-medium text-slate-500 mb-4">
            We encountered an error while fetching your account details.
          </p>
          <button onClick={fetchProfile} className="btn btn-primary w-full">
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen page-root">
      <div className="max-w-7xl mx-auto px-6">
        {/* Welcome Banner */}
        <div className="relative overflow-hidden rounded-3xl bg-primary px-8 py-10 mb-10 animate-slide-up shadow-2xl shadow-primary/20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-10 -mb-10 blur-2xl pointer-events-none" />

          <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <span className="inline-block text-[10px] font-bold text-white/60 uppercase tracking-[0.2em] mb-2 leading-none">
                {getGreeting()}
              </span>
              <h1 className="font-display text-3xl font-extrabold text-white tracking-tight leading-tight">
                Welcome back, {profile.firstName || "Seller"}! 👋
              </h1>
              <p className="text-white/70 text-sm font-medium mt-2">
                Here's a quick look at your store dashboard and profile details.
              </p>
            </div>

            <div className="flex flex-col items-center md:items-end gap-1">
              <div className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/10 text-white font-bold text-sm">
                {new Date().toLocaleDateString("en-IN", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest mt-1">
                Seller GSTID: {profile.sellerProfile?.gstin}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10 animate-slide-up">
          {stats.map((s, i) => (
            <div
              key={i}
              className="inner-card !p-6 flex items-start justify-between"
            >
              <div className="flex flex-col gap-1.5 grow">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                  {s.label}
                </span>
                <span className={`font-display text-2xl font-black ${s.color}`}>
                  {s.value}
                </span>
              </div>
              <div
                className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center ${s.color}`}
              >
                <s.icon size={20} />
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-slide-up">
          {/* Left Column (Main Info) */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            {/* Personal Information */}
            <div className="inner-card !p-0 overflow-hidden">
              <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/5 rounded-xl flex items-center justify-center text-primary">
                    <User size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      Personal Information
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                      Account owner details
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn btn-ghost !px-6 !py-2 h-10 text-xs font-bold"
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit Profile
                </button>
              </div>

              <div className="p-8 flex flex-col gap-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                      First Name
                    </span>
                    <span className="text-base font-extrabold text-slate-900 leading-tight">
                      {profile.firstName}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                      Last Name
                    </span>
                    <span className="text-base font-extrabold text-slate-900 leading-tight">
                      {profile.lastName}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2.5 p-4 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-primary/20 transition-all">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                      Email Address
                    </span>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 truncate">
                        <Mail size={16} className="text-slate-400 shrink-0" />
                        <span className="text-sm font-extrabold text-slate-900 truncate">
                          {profile.email}
                        </span>
                      </div>
                      {profile.emailVerified && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-500 rounded-full">
                          <CheckCircle2 size={12} />
                          <span className="text-[8px] font-black uppercase tracking-widest">
                            Verified
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2.5 p-4 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-primary/20 transition-all">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                      Phone Number
                    </span>
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-slate-400 shrink-0" />
                      <span className="text-sm font-extrabold text-slate-900">
                        {profile.phone || "Not provided"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="inner-card !p-0 overflow-hidden">
              <div className="px-8 py-6 border-b border-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary/5 rounded-xl flex items-center justify-center text-secondary">
                    <Building2 size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                      Business Details
                    </h2>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                      Business identification & tax
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-8 flex flex-col gap-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                      Business Name
                    </span>
                    <span className="text-base font-extrabold text-slate-900 leading-tight">
                      {profile.sellerProfile?.businessName || "Not provided"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                      Business Type
                    </span>
                    <span className="text-base font-extrabold text-slate-900 leading-tight capitalize">
                      {profile.sellerProfile?.businessType || "Not specified"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 p-5 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-primary/20 transition-all">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                    Business Address
                  </span>
                  <div className="flex items-start gap-3">
                    <MapPin
                      size={18}
                      className="text-slate-400 shrink-0 mt-0.5"
                    />
                    <span className="text-sm font-extrabold text-slate-900 leading-relaxed">
                      {profile.sellerProfile?.businessAddress || "Not provided"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    {
                      label: "GSTIN",
                      value: profile.sellerProfile?.gstin,
                      key: "gstin",
                    },
                    {
                      label: "PAN Number",
                      value: profile.sellerProfile?.panNumber,
                      key: "pan",
                    },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl group transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <FileText
                          size={16}
                          className="text-slate-400 shrink-0"
                        />
                        <div className="flex flex-col">
                          <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">
                            {item.label}
                          </span>
                          <span className="text-sm font-mono font-bold text-slate-900 tracking-tight">
                            {item.value || "Not provided"}
                          </span>
                        </div>
                      </div>
                      {item.value && (
                        <button
                          onClick={() => handleCopy(item.value!, item.key)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary hover:bg-white transition-all"
                        >
                          {copied === item.key ? (
                            <Check size={14} className="text-emerald-500" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Sidebars) */}
          <div className="flex flex-col gap-8">
            {/* Bank Details Sidebar */}
            <div className="inner-card !p-0 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-500">
                    <Landmark size={18} />
                  </div>
                  <span className="text-sm font-extrabold text-slate-900 tracking-tight">
                    Bank Details
                  </span>
                </div>
                <button
                  onClick={() => setShowBankModal(true)}
                  className="p-2 rounded-lg text-primary hover:bg-primary/5 transition-all"
                  title="Update Bank Info"
                >
                  <Edit2 size={16} />
                </button>
              </div>

              <div className="p-6 flex flex-col gap-4">
                <div className="flex flex-col gap-2.5 p-4 bg-slate-50 border border-slate-100 rounded-2xl group transition-all">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                    Account Number
                  </span>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 grow">
                      <CreditCard size={14} className="text-slate-400" />
                      <span className="text-sm font-mono font-bold text-slate-900 tracking-tight">
                        {profile.sellerProfile?.bankAccount
                          ? showAccount
                            ? profile.sellerProfile.bankAccount
                            : `••••••••${profile.sellerProfile.bankAccount.slice(-4)}`
                          : "Not provided"}
                      </span>
                    </div>
                    {profile.sellerProfile?.bankAccount && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowAccount(!showAccount)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary transition-all"
                        >
                          {showAccount ? (
                            <EyeOff size={14} />
                          ) : (
                            <Eye size={14} />
                          )}
                        </button>
                        <button
                          onClick={() =>
                            handleCopy(
                              profile.sellerProfile!.bankAccount,
                              "account",
                            )
                          }
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary transition-all"
                        >
                          {copied === "account" ? (
                            <Check size={14} className="text-emerald-500" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 p-4 bg-slate-50 border border-slate-100 rounded-2xl group transition-all">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none">
                    IFSC Code
                  </span>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 grow">
                      <Landmark size={14} className="text-slate-400" />
                      <span className="text-sm font-mono font-bold text-slate-900 tracking-tight uppercase">
                        {profile.sellerProfile?.ifscCode || "Not provided"}
                      </span>
                    </div>
                    {profile.sellerProfile?.ifscCode && (
                      <button
                        onClick={() =>
                          handleCopy(profile.sellerProfile!.ifscCode, "ifsc")
                        }
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-primary transition-all"
                      >
                        {copied === "ifsc" ? (
                          <Check size={14} className="text-emerald-500" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3.5 bg-amber-50 rounded-xl border border-amber-100">
                  <AlertCircle
                    size={14}
                    className="text-amber-500 shrink-0 mt-0.5"
                  />
                  <p className="text-[10px] font-medium text-amber-700 leading-relaxed">
                    Settlements are processed automatically to this account
                    every Friday.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions sidebar */}
            <div className="inner-card !p-0 overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-50 bg-slate-50/30">
                <span className="text-sm font-extrabold text-slate-900 tracking-tight">
                  Quick Actions
                </span>
              </div>
              <div className="p-3 flex flex-col">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    className="w-full flex items-center justify-between p-3.5 rounded-2xl hover:bg-slate-50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-xl transition-transform group-hover:scale-110 ${action.bg} ${action.color}`}
                      >
                        <action.icon size={18} />
                      </div>
                      <span className="text-sm font-bold text-slate-700 group-hover:text-primary transition-colors">
                        {action.label}
                      </span>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-slate-300 group-hover:text-primary transition-colors translate-x-0 group-hover:translate-x-1"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Help/Support info box */}
            {/* <div className="inner-card bg-mesh !border-none !p-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-white rounded-2xl shadow-xl flex items-center justify-center text-primary mb-4 transform -rotate-6">
                <HelpCircle size={32} />
              </div>
              <h4 className="text-sm font-extrabold text-slate-900 tracking-tight mb-2">
                Need specialized help?
              </h4>
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mb-6 leading-relaxed">
                Connect with our seller support team for expert guidance.
              </p>
              <button className="btn btn-primary w-full py-2.5 text-xs">
                Contact Support
              </button>
            </div> */}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
              <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                Edit Profile
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={handleProfileUpdate}
              className="p-8 flex flex-col gap-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                    First Name
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={profileForm.firstName}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        firstName: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                    Last Name
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={profileForm.lastName}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        lastName: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Phone Number
                </label>
                <div className="flex">
                  <div className="flex items-center justify-center px-4 bg-slate-50 border border-slate-200 border-r-0 rounded-l-xl text-sm font-bold text-slate-500">
                    +91
                  </div>
                  <input
                    className="form-input !rounded-l-none"
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) =>
                      setProfileForm({
                        ...profileForm,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                      })
                    }
                    maxLength={10}
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Business Name
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={profileForm.businessName}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      businessName: e.target.value,
                    })
                  }
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Business Type
                </label>
                <select
                  className="form-input"
                  value={profileForm.businessType}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      businessType: e.target.value,
                    })
                  }
                >
                  <option value="">Select business type</option>
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="distributor">Distributor</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Business Address
                </label>
                <textarea
                  className="form-input py-3 resize-none"
                  value={profileForm.businessAddress}
                  onChange={(e) =>
                    setProfileForm({
                      ...profileForm,
                      businessAddress: e.target.value,
                    })
                  }
                  rows={3}
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="grow btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="grow btn btn-primary shadow-xl shadow-primary/20"
                >
                  {updating ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Update Modal */}
      {showBankModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={handleCloseBankModal}
        >
          <div
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between">
              <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                Update Bank Details
              </h2>
              <button
                onClick={handleCloseBankModal}
                className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={handleBankUpdate}
              className="p-8 flex flex-col gap-6"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Account Number
                </label>
                <input
                  className="form-input !font-mono !tracking-widest"
                  type="text"
                  value={bankForm.bankAccount}
                  onChange={(e) =>
                    setBankForm({
                      ...bankForm,
                      bankAccount: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  disabled={otpSent}
                  placeholder="Enter account number"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  IFSC Code
                </label>
                <input
                  className="form-input !font-mono !tracking-widest !uppercase"
                  type="text"
                  value={bankForm.ifscCode}
                  onChange={(e) =>
                    setBankForm({
                      ...bankForm,
                      ifscCode: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, ""),
                    })
                  }
                  disabled={otpSent}
                  placeholder="E.g. SBIN0001234"
                  maxLength={11}
                  required
                />
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleRequestBankOTP}
                  disabled={
                    otpLoading || !bankForm.bankAccount || !bankForm.ifscCode
                  }
                  className="btn btn-primary w-full py-4 shadow-xl shadow-primary/20"
                >
                  {otpLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Sending OTP...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mail size={18} />
                      <span>Request OTP To Update</span>
                    </div>
                  )}
                </button>
              ) : (
                <div className="flex flex-col gap-6 animate-slide-up">
                  <div className="flex flex-col gap-2.5">
                    <div className="flex justify-between items-center px-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Verify OTP
                      </label>
                      <button
                        type="button"
                        onClick={handleRequestBankOTP}
                        disabled={otpCooldown > 0 || otpLoading}
                        className="text-[10px] font-bold text-primary disabled:text-slate-400 uppercase tracking-widest hover:underline transition-all"
                      >
                        {otpCooldown > 0
                          ? `Resend in ${otpCooldown}s`
                          : "Resend OTP"}
                      </button>
                    </div>
                    <input
                      className="form-input h-14 !text-center !text-2xl !font-black !tracking-[0.4em] !font-mono"
                      type="text"
                      value={bankForm.otp}
                      onChange={(e) =>
                        setBankForm({
                          ...bankForm,
                          otp: e.target.value.replace(/\D/g, "").slice(0, 6),
                        })
                      }
                      maxLength={6}
                      placeholder="••••••"
                      required
                      autoFocus
                    />
                    <p className="text-[10px] font-medium text-slate-400 text-center leading-relaxed">
                      OTP has been sent to your registered email address.
                      <br />
                      It is valid for 10 minutes.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={handleCloseBankModal}
                      className="grow btn btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        bankUpdating ||
                        !bankForm.otp ||
                        bankForm.otp?.length < 4
                      }
                      className="grow btn btn-primary shadow-xl shadow-primary/20"
                    >
                      {bankUpdating ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Updating...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <CheckCircle size={18} />
                          <span>Update Details</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
